import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { PortalDocumentContext } from '#components/datasheet-popover/portal-document-context.js';

import type { ReactNode } from 'react';

export function DatasheetPopoverDocumentPortal({
	targetDocument,
	onTargetDocumentClose,
	children,
}: {
	targetDocument: Document | null;
	onTargetDocumentClose: () => void;
	children: ReactNode;
}) {
	const portalDocument = targetDocument ?? document;
	const portalRoot = useMemo(() => {
		if (portalDocument === document) {
			return null;
		}

		const root = portalDocument.createElement('div');
		root.dataset['datasheetPopoverDocumentPortal'] = '';
		return root;
	}, [portalDocument]);

	useEffect(() => {
		const portalWindow = portalDocument.defaultView;
		if (portalWindow?.closed === true) {
			onTargetDocumentClose();
			return undefined;
		}

		if (portalRoot === null) {
			return undefined;
		}

		portalDocument.body.appendChild(portalRoot);

		const handleBeforeUnload = () => onTargetDocumentClose();
		portalWindow?.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			portalWindow?.removeEventListener('beforeunload', handleBeforeUnload);
			portalRoot.remove();
		};
	}, [onTargetDocumentClose, portalDocument, portalRoot]);

	if (portalDocument === document) {
		return children;
	}

	if (portalRoot === null) {
		return null;
	}

	return createPortal(<PortalDocumentContext value={portalDocument}>{children}</PortalDocumentContext>, portalRoot);
}
